import { useEffect, useState } from "react";
import {
    Button,
    Dialog,
    DialogActions,
    DialogBody,
    DialogContent,
    DialogSurface,
    DialogTitle,
    DialogTrigger,
    Spinner,
    Text
} from "@fluentui/react-components";
import { ArrowUndo20Regular } from "@fluentui/react-icons";
import { Link } from "react-router-dom";
import moment from "moment";
import { useTranslation } from "react-i18next";

import { PaymentImputation } from "../../../../models/Entities/CashFlow/Payment/PaymentImputation";
import { formatCurrencyAmountWithIssue } from "../../../../commons/currency/Currency.Functions";
import * as ComponentFunctions from './PaymentImputations.Function';
import "../../CashFlow.Detail.Styles.css";

const fmt = (amount: number | undefined | null, currency: string | null | undefined) =>
    formatCurrencyAmountWithIssue(amount ?? 0, currency || 'ARS');

/** Tono y etiqueta de cada tipo de movimiento que devuelve el API. */
const movementLook = (type: string | null): { tone: string; tag: string; label: string } => {
    switch (type) {
        case 'Pago':
            return { tone: 'pago', tag: 'ok', label: 'Pago' };
        case 'Imputacion de Pago':
            return { tone: 'imputacion', tag: 'info', label: 'Imputación' };
        case 'Anulacion de Movimiento':
            return { tone: 'anulacion', tag: 'bad', label: 'Anulación' };
        default:
            return { tone: 'default', tag: 'neutral', label: type || 'Movimiento' };
    }
};

/**
 * A qué deudas se aplicó un pago. Cada imputación dice la deuda, entre quiénes,
 * cuánto se aplicó (y cuánto es en la moneda de la deuda si difiere) y permite
 * deshacerla.
 */
const PaymentImputationsComponent = (props: ComponentFunctions.Props) => {
    const [t] = useTranslation();
    const [confirmRollbackId, setConfirmRollbackId] = useState<number | null>(null);

    useEffect(() => {
        if (props.paymentId) {
            props.GetPaymentImputations(props.paymentId);
            props.GetAllPersons();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.paymentId]);

    const renderImputation = (item: PaymentImputation) => {
        const look = movementLook(item.movementType);
        const hasDebt = item.debtId > 0;
        const crossCurrency = !!item.debtCurrencyCode && item.paymentCurrencyCode !== item.debtCurrencyCode;

        return (
            <div key={item.imputationId} className={`cfd-imputation cfd-imputation--${look.tone}`}>
                <div className="cfd-imputation__head">
                    <span className={`cfd-tag cfd-tag--${look.tag}`}>{look.label}</span>
                    {hasDebt
                        ? <span className="cfd-imputation__debt">Deuda #{item.debtId}</span>
                        : <span className="cfd-empty">Sin deuda asociada</span>}
                    {item.debt?.settlementId && (
                        <Link className="cfd-link" to={`/settlements/${item.debt.settlementId}`}>LIQ-{item.debt.settlementId}</Link>
                    )}
                    <span className="cfd-imputation__amount cfd-num">
                        {fmt(item.amountInPaymentCurrency, item.paymentCurrencyCode)}
                        {crossCurrency && <small>≈ {fmt(item.amountInDebtCurrency, item.debtCurrencyCode)} · TC {item.exchangeRate.toFixed(4)}</small>}
                    </span>
                </div>
                {hasDebt && (
                    <div className="cfd-imputation__meta">
                        <span><b>{props.NameOf(item.debt?.debtor)}</b> debe a <b>{props.NameOf(item.debt?.creditor)}</b></span>
                        {item.debt?.debtDate && <span>del {moment(item.debt.debtDate).format('DD/MM/YYYY')}</span>}
                        {item.debt?.amount > 0 && <span>por {fmt(item.debt.amount, item.debtCurrencyCode)}</span>}
                    </div>
                )}
                <div className="cfd-imputation__meta">
                    <span>Movimiento #{item.movementId}</span>
                    <span className="cfd-imputation__actions">
                        <button
                            type="button"
                            className="cfd-btn cfd-btn--danger"
                            disabled={props.isRollingBack}
                            title={t('cashflow.imputations.rollback', 'Deshacer')}
                            onClick={() => setConfirmRollbackId(item.imputationId)}
                        >
                            <ArrowUndo20Regular /> {t('cashflow.imputations.rollback', 'Deshacer')}
                        </button>
                    </span>
                </div>
            </div>
        );
    };

    return (
        <div className="cfd-root">
            {props.showTitle !== false && (
                <h4 className="cfd-section__title">
                    {t('cashflow.imputations.title', 'Imputaciones del pago')}
                    {!props.isLoadingImputations && ` · ${props.imputations.length}`}
                </h4>
            )}

            {props.isLoadingImputations && (
                <div className="cfd-state"><Spinner size="extra-tiny" /> {t('cashflow.imputations.loading', 'Cargando imputaciones…')}</div>
            )}

            {props.failOnLoadingImputations && (
                <div className="cfd-banner cfd-banner--error">{props.error?.ErrorMessage ?? 'No se pudieron cargar las imputaciones.'}</div>
            )}
            {props.successRollingBack === true && (
                <div className="cfd-banner cfd-banner--success">{t('cashflow.imputations.rollbackSuccess', 'Imputación revertida correctamente.')}</div>
            )}
            {props.failOnRollingBack && (
                <div className="cfd-banner cfd-banner--error">{t('cashflow.imputations.rollbackError', 'No se pudo revertir la imputación.')}</div>
            )}

            {!props.isLoadingImputations && (props.imputations.length === 0
                ? <span className="cfd-empty">{t('cashflow.imputations.noImputations', 'Este movimiento todavía no se aplicó a ninguna deuda.')}</span>
                : <div className="cfd-imputations">{props.imputations.map(renderImputation)}</div>
            )}

            <Dialog open={confirmRollbackId !== null} onOpenChange={(_, data) => { if (!data.open) setConfirmRollbackId(null); }}>
                <DialogSurface>
                    <DialogBody>
                        <DialogTitle>{t('cashflow.imputations.rollbackConfirmTitle', 'Deshacer imputación')}</DialogTitle>
                        <DialogContent>
                            <Text>{t('cashflow.imputations.rollbackConfirmMessage', 'El monto vuelve a quedar sin imputar y la deuda recupera su saldo. ¿Confirmás?')}</Text>
                        </DialogContent>
                        <DialogActions>
                            <DialogTrigger disableButtonEnhancement>
                                <Button appearance="secondary" onClick={() => setConfirmRollbackId(null)}>
                                    {t('cashflow.imputations.cancel', 'Cancelar')}
                                </Button>
                            </DialogTrigger>
                            <Button
                                appearance="primary"
                                icon={props.isRollingBack ? <Spinner size="extra-tiny" /> : <ArrowUndo20Regular />}
                                disabled={props.isRollingBack}
                                onClick={() => {
                                    if (confirmRollbackId !== null) {
                                        props.RollbackPaymentImputation(confirmRollbackId, props.paymentId);
                                        setConfirmRollbackId(null);
                                    }
                                }}
                            >
                                {t('cashflow.imputations.confirm', 'Deshacer')}
                            </Button>
                        </DialogActions>
                    </DialogBody>
                </DialogSurface>
            </Dialog>
        </div>
    );
};

export default ComponentFunctions.connector(PaymentImputationsComponent);
